import axiosInstance from "../services/axiosInstance";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { GET_MEMBER } from "../services/apiEndPoints";

/* ================== TYPES ================== */
export interface User {
  memberId: string;
  memberEmail: string;
  role: "admin" | "user";
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (token: string) => Promise<void>;
  logout: () => void;
}

/* ================== CONTEXT ================== */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

const fetchMe = async () => {
  const res = await axiosInstance.get(GET_MEMBER);
  return res.data as User;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Prevent double /me call in React 18 StrictMode
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const token = localStorage.getItem("token");

    if (!token) {
      setLoading(false);
      return;
    }

    const initAuth = async () => {
      try {
        const me = await fetchMe(); // 🔥 called once
        setUser(me);
      } catch (err) {
        localStorage.removeItem("token");
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  /* ================== ACTIONS ================== */
  const login = async (token: string) => {
    localStorage.setItem("token", token);
    setLoading(true);

    try {
      const me = await fetchMe();
      setUser(me);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  /* ================== VALUE ================== */
  const value: AuthContextType = {
    user,
    isAuthenticated: Boolean(user),
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/* ================== HOOK ================== */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
};
export default AuthContext;
